import { SendHorizontal } from 'lucide-react';
import { memo, useState } from 'react';

import { ProfileLanguageFlags } from '@/features/profile/components/profileLanguageFlags';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/utils/cn';

import { useSendDuoMessage } from '../../hooks/useSendDuoMessage';

export interface DuoChatInputProps {
  userId: string;
  languageCodes?: string[];
  onMessageSent?: () => void;
  className?: string;
}

export const DuoChatInput = memo((props: DuoChatInputProps) => {
  const { userId, languageCodes = [], onMessageSent, className } = props;
  const [message, setMessage] = useState('');
  const { sendMessage, isLoading, isReady } = useSendDuoMessage();

  const handleSend = async () => {
    if (message.trim() && userId) {
      await sendMessage(userId, message);
      setMessage('');
      onMessageSent?.();
    }
  };

  return (
    <div className={cn('flex w-full gap-2', className)}>
      <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/20 px-2.5 py-1.5 backdrop-blur-sm">
        {languageCodes.length > 0 && (
          <ProfileLanguageFlags
            languageCodes={languageCodes}
            className="text-lg"
            circleClassName="size-4"
          />
        )}
        <Input
          value={message}
          onChange={e => {
            setMessage(e.target.value);
          }}
          placeholder="Send a message..."
          className="h-7 border-0 bg-transparent p-0 px-2 text-sm text-white placeholder:text-white/50 focus-visible:ring-0 dark:bg-transparent [&]:focus-visible:shadow-none"
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          disabled={isLoading}
        />
        <Button
          onClick={handleSend}
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          disabled={!isReady || !message.trim()}
          loading={isLoading}
        >
          <SendHorizontal className="text-cold-300 size-4" />
        </Button>
      </div>
    </div>
  );
});
